/**
 * UpSurv 2.0 — the TYPED pairing code (DECISIONS D-019).
 *
 * ── Why a second representation of the same code ────────────────────────────
 * D-013 gave onboarding one channel: a QR on the office screen, scanned by the
 * phone's camera. In a lit office with a clean lens that is the fastest thing
 * there is. In the field it is not the only case:
 *
 *   the lens is scratched or greasy (it lives in a machine room)
 *   the hands shake and the frame never holds still long enough
 *   the phone refuses the camera permission, or the office is a basement
 *   the office screen is a laptop tilted away, glare across the whole panel
 *
 * A surveyor stuck on any of those has no way to link their phone at all, and
 * the office has no way to help them. So the SAME onboarding record carries a
 * second representation: a short code a human can read out and another human
 * can type. It is not a second, weaker path — it hashes into the same
 * `onboarding_links` row, is consumed by the same single guarded UPDATE, dies
 * at the same instant, and honours the same 24-hour window (D-013).
 *
 * ── The alphabet is chosen for a 60-year-old in a dim machine room ──────────
 * CONSONANTS ONLY: B C D F G H J K L M N P Q R S T V W X Y Z — 21 letters.
 *
 *   No digits at all. Not for entropy reasons — for the phone keyboard. A code
 *     mixing letters and digits forces the typist to switch keyboard modes
 *     mid-code, which is where a shaky hand loses its place.
 *   No vowels. Two payoffs at once: `A/E/I/O/U` removed takes `0/O` and `1/I`
 *     — the two classic misreads — out with them, and a vowel-free code cannot
 *     accidentally spell a word, including an offensive one, on an office
 *     screen in front of the person it belongs to.
 *   `L` survives because the characters it is confused with (`1`, `I`) are both
 *     gone; on its own it is unambiguous.
 *
 * Nine characters, shown as three groups of three — `BCD-FGH-JKL`. Three is the
 * chunk a person can hold in their head while looking from a screen to a phone
 * and back. Groups are cosmetic: `normalizePairCode` throws away every
 * separator, so a code typed with spaces, with dashes, or as one run all hash
 * to the same value.
 *
 * ── Entropy, stated rather than assumed ─────────────────────────────────────
 * 21^9 ≈ 7.94e11 ≈ 39.5 bits, live for at most 24 hours, single-use, and
 * guessable only online against an endpoint whose refusals are all logged. That
 * is comfortably stronger than the 20-bit/30-second and 20-bit/10-minute codes
 * the rest of the industry gates accounts with, and it is a DELIBERATE trade
 * against the 256-bit QR secret: nobody can type 43 base64url characters. The
 * residual risk — there is no per-code lockout, because a Worker has nowhere
 * stateless to keep a counter — is recorded in D-019.
 */

/* ─────────────────────────────── the alphabet ────────────────────────────── */

/** Consonants only. See the header for every exclusion and why. */
export const PAIR_CODE_ALPHABET = 'BCDFGHJKLMNPQRSTVWXYZ';

/** Nine characters. Three groups of three. */
export const PAIR_CODE_LENGTH = 9;

/** How many characters per printed group. */
export const PAIR_CODE_GROUP = 3;

/** The separator the office screen prints. Ignored entirely on the way in. */
export const PAIR_CODE_SEPARATOR = '-';

/**
 * The largest multiple of the alphabet size that fits in a byte.
 *
 * Rejection sampling, not `% 21`: 256 is not a multiple of 21, so a plain
 * modulo would make the first ten letters of the alphabet measurably more
 * likely than the last eleven. Bytes at or above this are thrown away and
 * redrawn, which costs a handful of extra bytes and removes the bias entirely.
 */
const REJECTION_CEILING = Math.floor(256 / PAIR_CODE_ALPHABET.length) * PAIR_CODE_ALPHABET.length;

/* ─────────────────────────────── generation ──────────────────────────────── */

/** Fills a byte array from a CSPRNG. Injectable so tests are deterministic. */
export type RandomBytes = (into: Uint8Array) => void;

function cryptoRandomBytes(into: Uint8Array): void {
  const source = globalThis.crypto;
  if (!source?.getRandomValues) {
    throw new Error('generatePairCode: no CSPRNG available');
  }
  source.getRandomValues(into);
}

/**
 * Mint one pairing code, unformatted (no separators).
 *
 * The CSPRNG is the same one behind `generateAccessToken`. This is a credential,
 * not a friendly identifier, and `Math.random` would make it guessable in a way
 * no amount of alphabet design could repair.
 */
export function generatePairCode(randomBytes: RandomBytes = cryptoRandomBytes): string {
  let out = '';
  // Drawn a chunk at a time rather than a byte at a time: one syscall for the
  // common case, and the loop only comes round again if rejection ate the pool.
  while (out.length < PAIR_CODE_LENGTH) {
    const pool = new Uint8Array(PAIR_CODE_LENGTH * 2);
    randomBytes(pool);
    for (const byte of pool) {
      if (byte >= REJECTION_CEILING) continue;
      out += PAIR_CODE_ALPHABET[byte % PAIR_CODE_ALPHABET.length];
      if (out.length === PAIR_CODE_LENGTH) break;
    }
  }
  return out;
}

/* ────────────────────────── normalising what is typed ────────────────────── */

/**
 * What a typed code becomes before it is hashed or compared.
 *
 * Case is folded and every SEPARATOR is discarded, so all of these are the same
 * code: `bcd-fgh-jkl`, `BCD FGH JKL`, `BCDFGHJKL`, `bcd fgh-jkl`. Spaces,
 * hyphens of every Unicode flavour, dots and stray punctuation all go, because
 * every one of them is something a person adds while reading aloud.
 *
 * Digits and vowels are deliberately KEPT. They can never be part of a code, so
 * keeping them is what lets `looksLikePairCode` reject them and
 * `pairCodeProblem` name the mistake. A normaliser that quietly deleted a
 * mistyped `0` would turn a nine-character typo into an eight-character mystery
 * and the screen would say "too short" about a code that is the right length.
 */
export function normalizePairCode(raw: string): string {
  return raw.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Is this exactly a pairing code, after normalising? */
export function looksLikePairCode(raw: string): boolean {
  const normalized = normalizePairCode(raw);
  if (normalized.length !== PAIR_CODE_LENGTH) return false;
  for (const character of normalized) {
    if (!PAIR_CODE_ALPHABET.includes(character)) return false;
  }
  return true;
}

/**
 * `BCDFGHJKL` → `BCD-FGH-JKL`. Display only; never hashed in this form.
 *
 * Applied to whatever it is given, so it can also format a half-typed code as
 * the surveyor goes — which is the point of it being separate from generation.
 */
export function formatPairCode(raw: string): string {
  const normalized = normalizePairCode(raw);
  const groups: string[] = [];
  for (let at = 0; at < normalized.length; at += PAIR_CODE_GROUP) {
    groups.push(normalized.slice(at, at + PAIR_CODE_GROUP));
  }
  return groups.join(PAIR_CODE_SEPARATOR);
}

/* ─────────────────────────── saying what is wrong ────────────────────────── */

/**
 * Why this typed code cannot be a pairing code — in a sentence, or `null` when
 * there is nothing wrong with it.
 *
 * Checked in the order the mistakes actually happen, and each one names the
 * mistake rather than the rule. "Invalid code" is the answer that leaves a
 * surveyor standing in an office reading the same nine characters over and over
 * looking for a difference that is not there; "there is no letter O in a setup
 * code" is the answer that ends it.
 *
 * Nothing here decides a redemption. The server does that, against a hash.
 */
export function pairCodeProblem(raw: string): string | null {
  const normalized = normalizePairCode(raw);

  if (normalized.length === 0) {
    return 'Type the setup code from the office screen.';
  }
  if (/[0-9]/.test(normalized)) {
    return 'A setup code is letters only — it never has numbers in it. Check the office screen.';
  }
  if (/[AEIOU]/.test(normalized)) {
    return 'A setup code never uses A, E, I, O or U. Check the office screen and type it again.';
  }
  if (normalized.length < PAIR_CODE_LENGTH) {
    return 'That is not the whole code. A setup code is nine letters, in three groups of three.';
  }
  if (normalized.length > PAIR_CODE_LENGTH) {
    return 'That is too long. A setup code is nine letters, in three groups of three.';
  }
  return null;
}
